import React from 'react';
import { Avatar, Button, Modal, Typography, theme } from 'antd';
import { BankOutlined, CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { AgencyMembership } from '../utils/activeMembership';
import { useLanguage } from '../contexts/LanguageContext';

const { Text } = Typography;

const Row = styled.button<{ $active: boolean; $hover: string; $activeBg: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: ${(p) => (p.$active ? p.$activeBg : 'transparent')};
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};
  text-align: left;
  font: inherit;
  color: inherit;
  &:hover {
    background: ${(p) => (p.$active ? p.$activeBg : p.$hover)};
  }
  &:focus-visible {
    outline: 2px solid currentColor;
  }
`;

interface ListProps {
  memberships: AgencyMembership[];
  currentId?: string | null;
  onSelect: (m: AgencyMembership) => void;
}

/**
 * Gmail-style account list: one row per agency of this login — logo, name and
 * the role the person has THERE (e.g. Pflegekraft in A, Agentur-Admin in B).
 */
export const AgencyMembershipList: React.FC<ListProps> = ({ memberships, currentId, onSelect }) => {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  return (
    <div role="listbox" aria-label={t('agencySwitch.title')} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {memberships.map((m) => {
        const active = m.id === currentId;
        return (
          <Row
            key={m.id}
            type="button"
            role="option"
            aria-selected={active}
            $active={active}
            $hover={token.colorFillTertiary}
            $activeBg={token.colorPrimaryBg}
            onClick={() => !active && onSelect(m)}
          >
            <Avatar
              size={36}
              src={m.agencyLogoUrl || undefined}
              icon={!m.agencyLogoUrl ? <BankOutlined /> : undefined}
              style={{ flexShrink: 0, background: m.agencyLogoUrl ? 'transparent' : token.colorPrimary }}
            />
            <span style={{ flex: 1, minWidth: 0 }}>
              <Text strong ellipsis={{ tooltip: m.agencyName }} style={{ display: 'block' }}>
                {m.agencyName || t('agencySwitch.noAgency')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(`sidebar.roles.${m.role}`)}
              </Text>
            </span>
            {active && <CheckOutlined style={{ color: token.colorPrimary }} aria-label={t('agencySwitch.current')} />}
          </Row>
        );
      })}
    </div>
  );
};

interface PickerProps {
  open: boolean;
  memberships: AgencyMembership[];
  onSelect: (m: AgencyMembership) => void;
  onLogout: () => void;
}

/** Shown after login when the person belongs to several agencies and none is remembered. */
export const AgencyPickerModal: React.FC<PickerProps> = ({ open, memberships, onSelect, onLogout }) => {
  const { t } = useLanguage();
  return (
    <Modal
      open={open}
      title={t('agencySwitch.title')}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={<Button onClick={onLogout}>{t('sidebar.logout')}</Button>}
      width={440}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('agencySwitch.pickerIntro')}
      </Text>
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <AgencyMembershipList memberships={memberships} onSelect={onSelect} />
      </div>
    </Modal>
  );
};
